import { useState, useEffect, useCallback, useRef } from 'react';
import './MetricsFlywheel.css';

interface MetricsFlywheelProps {
  apiBaseUrl: string;
}

const SIGNALS = [
  { icon: 'eye', text: 'Viewed: Summer dresses' },
  { icon: 'search', text: 'Searched: Beach vacation' },
  { icon: 'click', text: 'Clicked: Linen pants' },
  { icon: 'clock', text: 'Browsed: 3 min' },
];

const AI_MESSAGE = "I think you'd love our new linen collection — perfect for your beach trip!";

interface MetricDef {
  label: string;
  description: string;
  from: number;
  to: number;
  decimals: number;
  prefix: string;
  suffix: string;
}

const METRICS: MetricDef[] = [
  {
    label: 'Avg revenue per user',
    description: 'Shoppers convert more often and buy more when guided.',
    from: 0, to: 7, decimals: 0, prefix: '', suffix: 'x',
  },
  {
    label: 'Increase in AOV',
    description: 'Relevant bundles and suggestions increase basket size.',
    from: 0, to: 36, decimals: 0, prefix: '', suffix: '%',
  },
  {
    label: 'Increase in order size',
    description: 'Customers add more items per purchase thanks to thoughtful upsells.',
    from: 0, to: 12, decimals: 0, prefix: '', suffix: '%',
  },
  {
    label: 'Add to cart rate',
    description: 'Shoppers move from browsing to purchase intent faster.',
    from: 0, to: 8, decimals: 0, prefix: '', suffix: 'x',
  },
];

function SignalIcon({ type }: { type: string }) {
  const shared = {
    width: 12, height: 12, viewBox: '0 0 24 24', fill: 'none',
    stroke: 'currentColor', strokeWidth: 2,
    strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const,
  };
  switch (type) {
    case 'eye':
      return <svg {...shared}><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>;
    case 'search':
      return <svg {...shared}><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>;
    case 'click':
      return <svg {...shared}><path d="M15 3h6v6M14 10l6.1-6.1M9 21H3v-6M10 14l-6.1 6.1" /></svg>;
    case 'clock':
      return <svg {...shared}><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>;
    default:
      return null;
  }
}

export function MetricsFlywheel(_props: MetricsFlywheelProps) {
  const loopRef = useRef(true);
  const timeoutsRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  const rafRef = useRef(0);

  const [signalVisible, setSignalVisible] = useState<boolean[]>(() => SIGNALS.map(() => false));
  const [signalAbsorbed, setSignalAbsorbed] = useState<boolean[]>(() => SIGNALS.map(() => false));
  const [messageVisible, setMessageVisible] = useState(false);
  const [messageChars, setMessageChars] = useState(0);
  const [metricValues, setMetricValues] = useState<number[]>(() => METRICS.map(m => m.from));
  const [activeMetrics, setActiveMetrics] = useState<boolean[]>(() => METRICS.map(() => false));
  const [fading, setFading] = useState(false);

  const sleep = useCallback(
    (ms: number) =>
      new Promise<void>(resolve => {
        const id = setTimeout(resolve, ms);
        timeoutsRef.current.push(id);
      }),
    [],
  );

  const animateMetrics = useCallback(
    (duration: number) =>
      new Promise<void>(resolve => {
        const start = performance.now();
        const tick = (now: number) => {
          if (!loopRef.current) { resolve(); return; }
          const t = Math.min((now - start) / duration, 1);
          const eased = 1 - Math.pow(1 - t, 3);
          setMetricValues(METRICS.map(m => m.from + (m.to - m.from) * eased));
          if (t < 1) {
            rafRef.current = requestAnimationFrame(tick);
          } else {
            resolve();
          }
        };
        rafRef.current = requestAnimationFrame(tick);
      }),
    [],
  );

  const typeMessage = useCallback(async () => {
    for (let i = 1; i <= AI_MESSAGE.length; i++) {
      if (!loopRef.current) return;
      setMessageChars(i);
      await sleep(25);
    }
  }, [sleep]);

  const runLoop = useCallback(async () => {
    while (loopRef.current) {
      setSignalVisible(SIGNALS.map(() => false));
      setSignalAbsorbed(SIGNALS.map(() => false));
      setMessageVisible(false);
      setMessageChars(0);
      setMetricValues(METRICS.map(m => m.from));
      setActiveMetrics(METRICS.map(() => false));
      setFading(false);
      await sleep(600);

      // Signals appear
      for (let i = 0; i < SIGNALS.length; i++) {
        if (!loopRef.current) return;
        setSignalVisible(prev => prev.map((v, j) => j <= i ? true : v));
        await sleep(350);
      }
      await sleep(500);

      // Signals converge and fade out one by one
      if (!loopRef.current) return;
      for (let i = 0; i < SIGNALS.length; i++) {
        if (!loopRef.current) return;
        setSignalAbsorbed(prev => prev.map((v, j) => j === i ? true : v));
        await sleep(150);
      }
      await sleep(400);

      // AI message types out
      if (!loopRef.current) return;
      setMessageVisible(true);
      await sleep(200);
      await typeMessage();
      await sleep(600);

      // Metrics appear and count up
      for (let i = 0; i < METRICS.length; i++) {
        if (!loopRef.current) return;
        setActiveMetrics(prev => prev.map((v, j) => j <= i ? true : v));
        await sleep(150);
      }
      await sleep(200);

      if (!loopRef.current) return;
      await animateMetrics(2000);

      await sleep(3000);

      if (!loopRef.current) return;
      setFading(true);
      await sleep(800);
      await sleep(500);
    }
  }, [sleep, animateMetrics, typeMessage]);

  useEffect(() => {
    loopRef.current = true;
    runLoop();
    return () => {
      loopRef.current = false;
      timeoutsRef.current.forEach(clearTimeout);
      timeoutsRef.current = [];
      cancelAnimationFrame(rafRef.current);
    };
  }, [runLoop]);

  const fmt = (def: MetricDef, value: number) =>
    `${def.prefix}${value.toFixed(def.decimals)}${def.suffix}`;

  const showArrow = (i: number) =>
    activeMetrics[i] && metricValues[i] > METRICS[i].from + (METRICS[i].to - METRICS[i].from) * 0.08;

  return (
    <div className={`metrics-flywheel ${fading ? 'mf-fading' : ''}`}>
      {/* Browsing Signals */}
      <div className="mf-section">
        <div className="sc-section-label">Browsing Signals</div>
        <div className="mf-signal-list">
          {SIGNALS.map((sig, i) => (
            <div
              key={i}
              className={`sc-pill mf-signal ${signalVisible[i] ? 'mf-signal-visible' : ''} ${signalAbsorbed[i] ? 'mf-signal-absorbed' : ''}`}
            >
              <SignalIcon type={sig.icon} />
              {sig.text}
            </div>
          ))}
        </div>
      </div>

      {/* AI Message */}
      <div className={`mf-message ${messageVisible ? 'mf-message-visible' : ''}`}>
        <div className="sc-bubble sc-bubble-assistant mf-message-bubble">
          <span className="mf-message-text">{AI_MESSAGE.slice(0, messageChars)}</span>
          {messageVisible && messageChars < AI_MESSAGE.length && (
            <span className="mf-cursor" />
          )}
        </div>
      </div>

      {/* Conversion Metrics */}
      <div className="mf-section">
        <div className="sc-section-label">Conversion Lift</div>
        <div className="mf-metrics-grid">
          {METRICS.map((metric, i) => (
            <div
              key={i}
              className={`mf-metric sc-glass-subtle ${activeMetrics[i] ? 'mf-metric-active' : ''}`}
            >
              <div className="mf-metric-value">
                <span>{fmt(metric, metricValues[i])}</span>
                {showArrow(i) && (
                  <svg
                    className="mf-metric-arrow"
                    width="10"
                    height="10"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#16a34a"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M12 19V5M5 12l7-7 7 7" />
                  </svg>
                )}
              </div>
              <div className="mf-metric-label">{metric.label}</div>
              <div className="mf-metric-description">{metric.description}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
